import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../routes';

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: object | object[];
  noindex?: boolean;
  ogImage?: string;
}

/**
 * Injects a per-route <title>, meta description, canonical link and JSON-LD
 * schema. Titles/descriptions should stay within 50-60 / 140-160 chars —
 * callers own that budget, this component just renders what it's given.
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  path,
  jsonLd,
  noindex = false,
  ogImage = `${SITE_URL}/og-default.png`,
}) => {
  const canonical = `${SITE_URL}${path}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Take Home Pay Calculators UK" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};
